<mat-sidenav-container class="cdv2-page">

  <!-- ── Installation status drawer — page-scoped via DrawerService ── -->
  <mat-sidenav #cdDrawer class="cdv2-drawer" position="end" mode="over" (closedStart)="closeDrawer()">

    <div class="drawer-header" *ngIf="drawerPayload">
      <div class="drawer-title">
        <mat-icon class="drawer-title-icon">{{ drawerPayload.titleIcon }}</mat-icon>
        <span>{{ drawerPayload.title }}</span>
      </div>
      <button mat-icon-button (click)="closeDrawer()" class="drawer-close-btn" aria-label="Close drawer">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-divider></mat-divider>

    <div class="drawer-body" *ngIf="drawerPayload?.content?.length">
      <mat-accordion [multi]="true" class="software-accordion">
        <ng-container
          *ngFor="let categoryGroup of drawerPayload?.content; trackBy: drawerService.trackByCategory.bind(drawerService)">
          <mat-expansion-panel expanded class="software-panel">
            <mat-expansion-panel-header class="software-panel-header">
              <mat-icon class="category-icon">{{ categoryGroup.icon }}</mat-icon>
              <span class="category-name">{{ categoryGroup.category }}</span>
            </mat-expansion-panel-header>

            <div class="category-items">
              <div *ngFor="let item of categoryGroup.items; trackBy: drawerService.trackByItemId.bind(drawerService)"
                class="item-row">
                <mat-icon class="item-icon" [ngClass]="drawerService.getStatusIconClass(item.status)"
                  [matTooltip]="item.error || undefined" matTooltipShowDelay="200">
                  {{ drawerService.getStatusIcon(item.status) }}
                </mat-icon>
                <span class="item-name">{{ item.name }}</span>
                <span class="item-status-badge" [ngClass]="drawerService.getStatusBadgeClass(item.status)"
                  [matTooltip]="item.error || undefined" matTooltipShowDelay="200">
                  {{ item.status }}
                </span>
              </div>
            </div>

          </mat-expansion-panel>
        </ng-container>
      </mat-accordion>
    </div>

  </mat-sidenav>

  <mat-sidenav-content>
    <!-- Loading state -->
    <div *ngIf="isLoading" class="cdv2-loading">
      <mat-progress-spinner mode="indeterminate" diameter="48"></mat-progress-spinner>
      <p class="cdv2-loading-text">Loading class details…</p>
    </div>

    <!-- Error state -->
    <div *ngIf="!isLoading && loadError" class="cdv2-error">
      <mat-icon class="cdv2-error-icon">error_outline</mat-icon>
      <p>{{ loadError }}</p>
      <button mat-stroked-button color="primary" (click)="goBack()">Go Back</button>
    </div>

    <!-- Main content -->
    <ng-container *ngIf="!isLoading && !loadError && classEntity">
      <!-- Page header -->
      <div class="cdv2-header">
        <button mat-icon-button (click)="goBack()" matTooltip="Back" class="cdv2-back-button">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <div class="cdv2-header-info">
          <h1 class="cdv2-title">{{ classEntity.trainingSchedule?.title || classEntity.name }}</h1>
        </div>
        <span [class]="'cdv2-status-badge cdv2-status-' + (classEntity.status?.toLowerCase() || 'unknown')">
          {{ classEntity.status || 'Unknown' }}
        </span>
      </div>

      <!-- Material tabs -->
      <mat-tab-group [selectedIndex]="selectedTabIndex" (selectedIndexChange)="onTabChange($event)"
        animationDuration="200ms" class="cdv2-tab-group">

        <!-- Details tab -->
        <mat-tab>
          <ng-template mat-tab-label>
            <mat-icon class="cdv2-tab-icon">info_outline</mat-icon>
            Details
          </ng-template>
          <app-detail-tab [classEntity]="classEntity" [classId]="classId" [userId]="userId" [userRole]="userRole"
            [userName]="userName" [hasVmResource]="hasVmResource" [hasAccResource]="hasAccResource"
            [isVmTemplateReady]="isVmTemplateReady" [hasSnapshot]="hasSnapshot">
          </app-detail-tab>
        </mat-tab>

        <!-- Activity tab — hidden for student/participant roles -->
        <mat-tab *ngIf="canViewActivityAndParticipants">
          <ng-template mat-tab-label>
            <mat-icon class="cdv2-tab-icon">monitor_heart</mat-icon>
            Activity
          </ng-template>
          <app-activity-tab [classId]="classId" [userRole]="userRole" [userName]="userName"
            [hasVmResource]="hasVmResource" [hasSnapshot]="hasSnapshot"
            [vmConnectionTemplateId]="(classEntity && classEntity.vmConnectionTemplateId) || null"
            (goToSettingsVMTemplate)="handleGoToSettingsVMTemplate()">
          </app-activity-tab>
        </mat-tab>

        <!-- Roster Sheet tab — hidden for student/participant roles -->
        <mat-tab *ngIf="canViewActivityAndParticipants">
          <ng-template mat-tab-label>
            <mat-icon class="cdv2-tab-icon">people_outline</mat-icon>
            Roster Sheet
          </ng-template>
          <app-participant-details-tab [classId]="classId" [classEntity]="classEntity"
            (goToSettingsParticipants)="handleGoToSettingsParticipants()">
          </app-participant-details-tab>
        </mat-tab>

        <!-- Settings tab (rights-gated) -->
        <mat-tab *ngIf="canAccessSettings">
          <ng-template mat-tab-label>
            <mat-icon class="cdv2-tab-icon">settings</mat-icon>
            Settings
          </ng-template>
          <app-settings-tab [classEntity]="classEntity" [classId]="classId" [hasVmResource]="hasVmResource"
            [hasAccResource]="hasAccResource" [isVmTemplateReady]="isVmTemplateReady" [hasSnapshot]="hasSnapshot"
            [hasCloudSettings]="canAccessSettings" [initialSection]="settingsInitialSection"
            (snapshotDone)="hasSnapshot = true" (initialSectionConsumed)="settingsInitialSection = null"
            (subscriptionsChanged)="reloadClassSilent()" (accSubscriptionsChanged)="reloadClassSilent()"
            (tenantConfigured)="reloadClassSilent()" (classDataRefreshNeeded)="reloadClassSilent()">
          </app-settings-tab>
        </mat-tab>

        <!-- Resources tab (rights-gated — same access level as Settings) -->
        <mat-tab *ngIf="canAccessSettings">
          <ng-template mat-tab-label>
            <mat-icon class="cdv2-tab-icon">dns</mat-icon>
            Resources
          </ng-template>
          <app-resources-tab [classId]="classId" [classEntity]="classEntity" [hasVmResource]="hasVmResource"
            [hasAccResource]="hasAccResource" (templateVmChanged)="reloadClassSilent()">
          </app-resources-tab>
        </mat-tab>

        <!-- Cost Analysis tab (rights-gated — same access level as Settings) -->
        <mat-tab *ngIf="canAccessSettings">
          <ng-template mat-tab-label>
            <mat-icon class="cdv2-tab-icon">bar_chart</mat-icon>
            Cost Analysis
          </ng-template>
          <app-cost-analysis-tab [classId]="classId" [classTitle]="classEntity.name">
          </app-cost-analysis-tab>
        </mat-tab>

      </mat-tab-group>
    </ng-container>

  </mat-sidenav-content>
</mat-sidenav-container>